import React, { useEffect } from "react";
import { useTranslate, useCreate, useApiUrl, useCustomMutation } from "@refinedev/core";
import { Form, Input, Modal, Typography, Space, Button, Card, Row, Col, message } from "antd";
import { ApiOutlined, SaveOutlined } from "@ant-design/icons";

const { Title } = Typography;

type ServiceProviderCreateModalProps = {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
};

export const ServiceProviderCreateModal: React.FC<ServiceProviderCreateModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const _t = useTranslate();
  const apiUrl = useApiUrl();
  const [form] = Form.useForm();

  const { mutate: create, isLoading: isCreating } = useCreate();
  const { mutate: testAuthentication, isLoading: isTestingAuth } = useCustomMutation();

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleFinish = (values: any) => {
    create(
      {
        resource: "service-providers",
        values,
      },
      {
        onSuccess: () => {
          form.resetFields();
          onCancel();
          onSuccess?.();
        },
      },
    );
  };

  const handleTestAuthentication = async () => {
    try {
      const values = await form.validateFields(["apiEndpoint"]);
      const clientId = String(values.clientId ?? "").trim();
      const clientSecret = String(values.clientSecret ?? "").trim();
      if (!clientId || !clientSecret) {
        const fieldErrors: { name: string; errors: string[] }[] = [];
        if (!clientId) {
          fieldErrors.push({
            name: "clientId",
            errors: [_t("service-providers.errors.credentialsRequired")],
          });
        }
        if (!clientSecret) {
          fieldErrors.push({
            name: "clientSecret",
            errors: [_t("service-providers.errors.credentialsRequired")],
          });
        }
        form.setFields(fieldErrors);
        return;
      }
      testAuthentication(
        {
          url: `${apiUrl}/service-providers/test-authentication`,
          method: "post",
          values: {
            apiEndpoint: values.apiEndpoint,
            clientId,
            clientSecret,
          },
        },
        {
          successNotification: false,
          errorNotification: false,
          onSuccess: () => {
            message.success(_t("service-providers.notifications.testAuthSuccess"));
          },
          onError: () => {
            message.error(_t("service-providers.notifications.testAuthError"));
          },
        },
      );
    } catch {
      // validation errors are shown by the form
    }
  };

  return (
    <Modal
      title={_t("service-providers.titles.create")}
      open={open}
      onCancel={onCancel}
      width={700}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onCancel}>{_t("buttons.cancel")}</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isCreating}
            onClick={() => form.submit()}
          >
            {_t("buttons.save")}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="!mt-4"
      >
        <Form.Item
          label={_t("service-providers.fields.name")}
          name="name"
          rules={[{ required: true, message: _t("fields.errors.required") }]}
        >
          <Input placeholder={_t("service-providers.fields.name")} />
        </Form.Item>

        <Card size="small">
          <Title level={5}>{_t("service-providers.fields.apiAndCredentials")}</Title>
          <Form.Item
            label={_t("service-providers.fields.apiEndpoint")}
            name="apiEndpoint"
            rules={[
              { type: "url", message: _t("service-providers.errors.invalidUrl") },
            ]}
          >
            <Input placeholder="https://api.example.com" />
          </Form.Item>
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Form.Item
                label={_t("service-providers.fields.clientId")}
                name="clientId"
              >
                <Input placeholder={_t("service-providers.fields.clientId")} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label={_t("service-providers.fields.clientSecret")}
                name="clientSecret"
              >
                <Input.Password
                  placeholder={_t("service-providers.fields.clientSecret")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Button
            htmlType="button"
            icon={<ApiOutlined />}
            loading={isTestingAuth}
            onClick={handleTestAuthentication}
          >
            {_t("service-providers.actions.testAuthentication")}
          </Button>
        </Card>
      </Form>
    </Modal>
  );
}

